export interface FaqLink {
  to: string;
  label: string;
}

export interface FaqItem {
  id: string;
  category: string;
  question: string;
  answer: string;
  link?: FaqLink;
}

export const faqItems: FaqItem[] = [
  // Cost
  {
    id: 'free',
    category: 'Cost',
    question: 'Is the EA really free? What is the catch?',
    answer:
      'Yes — GoldWarden EA is completely free. There is no license fee, no monthly subscription and no hidden profit-sharing. You get the compiled EA file (.ex5) and a personal preset for your account size. We offer it free because we trade it ourselves and enjoy feedback from the community.',
    link: { to: '/get-ea', label: 'Claim the EA free' },
  },
  {
    id: 'balance',
    category: 'Cost',
    question: 'What is the minimum deposit to run the EA?',
    answer:
      'We recommend a minimum of $500 to follow the risk presets correctly. On a $500 account the EA trades the smallest safe lot sizes. It scales smoothly all the way up to six-figure accounts because position size is calculated as a fixed fraction of equity.',
    link: { to: '/get-ea', label: 'See the balance presets' },
  },
  {
    id: 'updates',
    category: 'Cost',
    question: 'How do I get future updates?',
    answer:
      'Updates are free too. When we release a new version, we message everyone in the support chat with the new .ex5 file plus updated presets. Your existing settings carry over automatically, so you stay current with a single file swap.',
  },
  {
    id: 'multiple-accounts',
    category: 'Cost',
    question: 'Can I run it on more than one account, or share it with a friend?',
    answer:
      'Yes. Because GoldWarden is completely free, there is no per-account licence and nothing to activate — install it on as many of your own accounts as you like, live or demo, and feel free to pass the file on to a friend. The only thing we ask is that you do not resell it or bundle it into a paid product. For each account, just load the preset that matches that balance.',
  },

  // Broker & setup
  {
    id: 'account-access',
    category: 'Broker & setup',
    question: 'Do you need my broker login, or does the EA run on my own account?',
    answer:
      'The EA runs entirely on your own account and your own machine — a PC, a Mac or a VPS. We never ask for your broker login, investor password or any account access, and we never trade on your behalf. You install the file, you keep full control, and you can remove it whenever you like. That is also why giving it away is safe for us: we simply never touch your account.',
  },
  {
    id: 'file-safety',
    category: 'Broker & setup',
    question: 'Is the .ex5 file safe to install? Does it steal data or call home?',
    answer:
      'It is a standard compiled MetaTrader 5 expert advisor, the same file type as any EA you would buy. There is no installer, no separate program and no third-party server — it can only read your account through MetaTrader itself and send orders through the terminal. It cannot reach your files, your browser or anything outside the platform, and it never needs your login or investor password. If you want to check, its every action is written to the MT5 Experts and Journal logs on your own machine.',
    link: { to: '/get-ea', label: 'Get the EA and setup guide' },
  },
  {
    id: 'broker',
    category: 'Broker & setup',
    question: 'Which broker and account type do I need?',
    answer:
      'You need any MetaTrader 5 account that offers XAUUSD with a raw or low spread. An ECN / Raw Spread account with a fast server gives the best execution. You do not need to use our broker — the EA is broker-agnostic and works on hedging or netting accounts.',
  },
  {
    id: 'mt4',
    category: 'Broker & setup',
    question: 'Does it work on MetaTrader 4, or only MT5?',
    answer:
      'GoldWarden is built for MetaTrader 5 only. It relies on features that MT4 simply does not have, so there is no MT4 version and none is planned. If you are still on MT4, open a free MT5 account at your broker — most offer both platforms under the same login, and your XAUUSD symbols and server stay the same.',
    link: { to: '/how-it-works', label: 'See the MT5 setup steps' },
  },
  {
    id: 'vps',
    category: 'Broker & setup',
    question: 'Do I need a VPS to run it?',
    answer:
      'A VPS is strongly recommended so the EA keeps trading when your computer is off. GoldWarden runs on Windows VPS with MetaTrader 5, needs no software dependencies, and uses very little CPU. Any entry-level VPS near your broker server is enough.',
  },
  {
    id: 'install',
    category: 'Broker & setup',
    question: 'How long does setup take?',
    answer:
      'Around five minutes. Drop the .ex5 file into your MT5 Experts folder, load the preset we send you, drag it onto an XAUUSD M15 chart and enable AutoTrading. We include a step-by-step guide and can walk you through it live in the support chat.',
    link: { to: '/get-ea', label: 'Get the EA and setup guide' },
  },
  {
    id: 'demo',
    category: 'Broker & setup',
    question: 'Can I try it on a demo account first?',
    answer:
      'Yes, and we recommend it. Load the preset on a demo account and let it run for a week or two, so you can watch how it enters, manages stops and behaves on quiet days before you risk real money. The EA runs identically on demo and live accounts — the only differences are spreads and execution speed.',
    link: { to: '/get-ea', label: 'Claim the EA free' },
  },

  // Risk & results
  {
    id: 'strategy',
    category: 'Risk & results',
    question: 'Does the EA use martingale or grid?',
    answer:
      'No. GoldWarden EA never uses martingale, grid recovery, or averaging down. Every trade opens with a hard stop-loss already attached and risks a fixed percentage of your account. Losing streaks are contained by design, not by adding to losers.',
    link: { to: '/how-it-works', label: 'See the entry and stop logic' },
  },
  {
    id: 'drawdown',
    category: 'Risk & results',
    question: 'How much drawdown should I expect?',
    answer:
      'Risk is preset as a fixed share of equity — typically 0.5% to 1.5% per trade depending on the preset you choose. With those settings, occasional losing streaks of several trades in a row are normal, and you should be prepared for a peak-to-trough drawdown in the low double digits on the higher-risk preset. Never risk money you cannot afford to lose, and step down to a lower preset if the swings feel uncomfortable.',
    link: { to: '/performance', label: 'See the live drawdown record' },
  },
  {
    id: 'prop-firm',
    category: 'Risk & results',
    question: 'Can I run it on a funded or prop firm account?',
    answer:
      'Yes — plenty of users run GoldWarden on funded accounts. Choose the lowest risk preset, because prop firms enforce strict daily and overall drawdown limits and the EA\u2019s fixed-risk model is designed around that. Check your firm\u2019s rules on expert advisors and news filtering before you start, as some impose restrictions, and remember we can\u2019t be responsible for breaching a firm\u2019s terms.',
  },
  {
    id: 'offline',
    category: 'Risk & results',
    question: 'What happens to my open trades if my PC or VPS goes offline?',
    answer:
      'Every position is opened with its stop-loss and take-profit already stored on the broker\u2019s server, so those levels stay active even if your machine disconnects — the trade remains protected. The EA simply resumes managing it once you reconnect. This is the main reason we recommend a VPS: to avoid gaps in trade management during a power cut or restart.',
  },
  {
    id: 'weekend',
    category: 'Risk & results',
    question: 'Does it hold trades over the weekend? What about gold gaps?',
    answer:
      'Gold does not trade on weekends, so there is nothing to manage between the Friday close and the Monday open. The EA avoids opening new trades into the weekend and never relies on being able to exit during a gap — every position already carries its broker-side stop-loss, so if the market reopens with a gap the stop is filled at the first available price. If you would rather be flat over the weekend, you can close manually before the close and the EA simply starts fresh on Monday.',
  },
  {
    id: 'performance',
    category: 'Risk & results',
    question: 'Are the performance numbers real?',
    answer:
      'The stats shown on the Performance page come straight from a live MetaTrader 5 account that we trade ourselves. We publish the full record here — including the losing months — instead of a hand-picked best case. Past performance never guarantees future results — please treat every figure as historical information, not a promise.',
    link: { to: '/performance', label: 'See the live performance record' },
  },
];